import type { ISetOperatorSettingsCmd } from "@/data/commands/user/SetOperatorSettingsCmd";
import { Factory } from "@/data/dao/Factory";
import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import { UserDao } from "@/data/dao/service/UserDao";
import {
    FulfilmentTileSize,
    NewOrdersViewMode,
    OperatorPlaySoundMode,
    OrderStateMode,
    ProductionTileViewMode,
} from "@/data/model/Enums";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";
import type { ILocation } from "@/data/model/Location";
import type { IRoom } from "@/data/model/Room";
import type { IOperatorSettings } from "@/data/model/User";
import type { IFulfillmentGroupsQry } from "@/data/queries/catalog/FulfillmentGroupsQry";
import type { IRoomsQry } from "@/data/queries/room/RoomsQry";
import { Misc } from "@/data/util/Misc";

import { getLocationName } from "@/shared/helpers/LocationHelper";
import { translate } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { Select } from "@/portal/formControls/Select";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";

export class UserOperatorSettingsForm extends BaseEntityForm<IOperatorSettings, ISetOperatorSettingsCmd> {
    private locationSelector: Select<ILocation>;
    private planRoomSelector: Select<IRoom>;
    private orderRoomSelector: Select<IRoom>;
    private fulfillmentGroupSelector: Select<IFulfillmentGroup>;
    private newOrdersViewModeSelector: Select<NewOrdersViewMode>;
    private fulfilmentTileSizeSelector: Select<FulfilmentTileSize>;
    private productionTileViewModeSelector: Select<ProductionTileViewMode>;
    private orderStateModeSelector: Select<OrderStateMode>;
    private operatorPlaySoundModeSelector: Select<OperatorPlaySoundMode>;

    private supressLocationChangeEvent = false;
    private currentLocationId?: string;

    constructor(element: JQuery) {
        super(element, {
            getSingle: undefined as any,
            create: undefined as any,
            getData: undefined as any,
            remove: undefined as any,
            update: (i, d) => Factory.getInstance<UserDao>(UserDao).setOperatorSettings(i, d),
        });

        const locationSelector = this.$form.find('.form-control[name="location"]');
        this.locationSelector = Select.createFromSelect(
            locationSelector,
            {
                getData: () =>
                    Factory.getInstance<LocationDao>(LocationDao).getData({
                        limit: 999,
                        asUser: this.id,
                    }),
            },
            (c) => getLocationName(c),
            true,
            i18n("general.locationSwitch.pleaseSelect")
        );
        this.locationSelector.onChange = (newVal) => {
            if (this.supressLocationChangeEvent) return;

            this.currentLocationId = newVal;
            this.loadSubEntities(() => {
                this.setSubEntityValue(undefined);
            });
        };

        const planRoomSelector = this.$form.find('.form-control[name="planroom"]');
        this.planRoomSelector = Select.createFromSelect(
            planRoomSelector,
            this.roomDataSource,
            (c) => c.name,
            true,
            i18n("elements.select.pleaseSelect")
        );

        const orderRoomSelector = this.$form.find('.form-control[name="orderrooms"]');
        this.orderRoomSelector = Select.createFromSelect(
            orderRoomSelector,
            this.roomDataSource,
            (c) => c.name,
            true,
            i18n("general.all"),
            undefined,
            true
        );

        const operatorPlaySoundModeSelector = this.$form.find('.form-control[name="operator-play-sound-mode"]');
        Misc.eachEnum(OperatorPlaySoundMode, (i, e) => {
            operatorPlaySoundModeSelector.append(
                '<option value="' + i + '">' + i18n("enums.OperatorPlaySoundMode." + e) + "</option>"
            );
        });

        this.operatorPlaySoundModeSelector = Select.createFromSelect(
            operatorPlaySoundModeSelector,
            undefined,
            undefined,
            false,
            i18n("general.none"),
            undefined,
            true
        );

        const fulfillmentGroupSelector = this.$form.find('.form-control[name="fulfillmentgroups"]');
        this.fulfillmentGroupSelector = Select.createFromSelect(
            fulfillmentGroupSelector,
            this.fulfillmentGroupDataSource,
            (c) => (c.internalName === undefined || c.internalName === null ? translate(c.name) : c.internalName),
            true,
            i18n("general.all"),
            undefined,
            true
        );

        const fulfilmentTileSizeSelector = this.$form.find('.form-control[name="fulfilmenttilesize"]');
        Misc.eachEnum(FulfilmentTileSize, (i, e) => {
            fulfilmentTileSizeSelector.append(
                '<option value="' + i + '">' + i18n("enums.FulfilmentTileSize." + e) + "</option>"
            );
        });

        this.fulfilmentTileSizeSelector = Select.createFromSelect(fulfilmentTileSizeSelector);

        const productionTileViewModeSelector = this.$form.find('.form-control[name="productionTileViewMode"]');
        Misc.eachEnum(ProductionTileViewMode, (i, e) => {
            productionTileViewModeSelector.append(
                '<option value="' + i + '">' + i18n("enums.ProductionTileViewMode." + e) + "</option>"
            );
        });

        this.productionTileViewModeSelector = Select.createFromSelect(productionTileViewModeSelector);

        const orderStateModeSelector = this.$form.find('.form-control[name="orderstatemode"]');
        Misc.eachEnum(OrderStateMode, (i, e) => {
            orderStateModeSelector.append(
                '<option value="' + i + '">' + i18n("enums.OrderStateMode." + e) + "</option>"
            );
        });

        this.orderStateModeSelector = Select.createFromSelect(orderStateModeSelector);

        const newOrdersViewModeSelector = this.$form.find('.form-control[name="newordersviewmode"]');
        Misc.eachEnum(NewOrdersViewMode, (i, e) => {
            newOrdersViewModeSelector.append(
                '<option value="' + i + '">' + i18n("enums.NewOrdersViewMode." + e) + "</option>"
            );
        });

        this.newOrdersViewModeSelector = Select.createFromSelect(newOrdersViewModeSelector);
    }

    public loadForUser(userId: string): UserOperatorSettingsForm {
        this.id = userId;

        Factory.getInstance<UserDao>(UserDao)
            .getOperatorSettings(userId)
            .then((settings) => {
                this.locationSelector.fetchData(() => {
                    this.setFormData(settings);
                });
            });

        return this;
    }

    public setFormData(model?: IOperatorSettings) {
        let hasLocationChanges = false;

        const newLocationId = model === undefined ? undefined : model.location?.id;
        if (this.currentLocationId !== newLocationId) {
            this.currentLocationId = newLocationId;
            hasLocationChanges = true;
            this.supressLocationChangeEvent = true;
            this.locationSelector.setValue(model === undefined ? undefined : model.location?.id);
            this.supressLocationChangeEvent = false;
        }

        const newOrdersViewMode =
            model?.newOrdersViewMode == null ? NewOrdersViewMode.AtTheTop : model.newOrdersViewMode;
        this.newOrdersViewModeSelector.setValue(newOrdersViewMode);
        this.fulfilmentTileSizeSelector.setValue(
            model?.fulfilmentTileSize == null ? FulfilmentTileSize.Normal : model.fulfilmentTileSize
        );

        this.productionTileViewModeSelector.setValue(
            model?.productionTileViewMode == null ? ProductionTileViewMode.Detailed : model.productionTileViewMode
        );

        const orderState = model?.orderStateMode == null ? OrderStateMode.All : model.orderStateMode;
        this.orderStateModeSelector.setValue(orderState);

        this.operatorPlaySoundModeSelector.setValues(
            model?.operatorPlaySoundModesList ?? [OperatorPlaySoundMode.FulfillmentInPreparation]
        );
        if (hasLocationChanges) {
            this.loadSubEntities(() => {
                this.setSubEntityValue(model);
            });
        } else this.isReady = true;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ISetOperatorSettingsCmd {
        const data: ISetOperatorSettingsCmd = {
            locationId: this.locationSelector.getValue()!,
            planRoomId: this.planRoomSelector.getValue(),
            fulfillmentGroupIds: this.fulfillmentGroupSelector.getValues(),
            orderRoomIds: this.orderRoomSelector.getValues(),
            newOrdersViewMode: parseInt(this.newOrdersViewModeSelector.getValue()!),
            fulfilmentTileSize: parseInt(this.fulfilmentTileSizeSelector.getValue()!),
            orderStateMode: parseInt(this.orderStateModeSelector.getValue()!),
            productionTileViewMode: parseInt(this.productionTileViewModeSelector.getValue()!),
            operatorPlaySoundModes: this.operatorPlaySoundModeSelector.getValues().map((c) => parseInt(c)),
        };

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");

        if (data.locationId === undefined) {
            hasError = true;
            this.locationSelector.setError(true);
        }

        if (data.planRoomId === undefined) {
            hasError = true;
            this.planRoomSelector.setError(true);
        }

        if (data.fulfilmentTileSize === undefined) {
            hasError = true;
            this.fulfilmentTileSizeSelector.setError(true);
        }

        return (hasError ? undefined : data) as any;
    }

    private setSubEntityValue(model?: IOperatorSettings) {
        if (model !== undefined) {
            this.planRoomSelector.setValue(model.planRoom === undefined ? undefined : model.planRoom.id);
            this.orderRoomSelector.setValues(model.orderRooms.map((d) => d.id));
            this.fulfillmentGroupSelector.setValues(model.fulfillmentGroups.map((d) => d.id));
        } else {
            this.planRoomSelector.setValue(undefined);
            this.orderRoomSelector.setValues([]);
            this.fulfillmentGroupSelector.setValues([]);
        }
    }

    private loadSubEntities(ready?: { () }) {
        this.isReady = false;
        let finishedLoaders = 0;
        const checkLoaders = () => {
            finishedLoaders++;
            if (finishedLoaders >= 3) {
                this.isReady = true;
                if (ready !== undefined) ready();
            }
        };

        this.planRoomSelector.fetchData(() => {
            checkLoaders();
        });

        this.orderRoomSelector.fetchData(() => {
            checkLoaders();
        });

        this.fulfillmentGroupSelector.fetchData(() => {
            checkLoaders();
        });
    }

    private roomDataSource: IDataSource<IRoom> = {
        getData: (filter: IRoomsQry) => {
            filter.locationId = this.locationSelector.getValue();
            return new Promise((resolve, reject) => {
                if (filter.locationId === undefined) {
                    resolve({
                        items: [],
                        limit: filter.limit!,
                        offset: filter.offset!,
                        hasPrev: false,
                        hasNext: false,
                    });
                    return;
                }

                return Factory.getInstance<RoomDao>(RoomDao)
                    .getData(filter)
                    .then((s) => {
                        resolve(s);
                    })
                    .catch((ex) => {
                        logError(ex);
                        reject();
                    });
            });
        },
    };

    private fulfillmentGroupDataSource: IDataSource<IFulfillmentGroup> = {
        getData: (filter: IFulfillmentGroupsQry) => {
            filter.locationId = this.locationSelector.getValue();
            return new Promise((resolve, reject) => {
                if (filter.locationId === undefined) {
                    resolve({
                        items: [],
                        limit: filter.limit!,
                        offset: filter.offset!,
                        hasPrev: false,
                        hasNext: false,
                    });
                    return;
                }

                return Factory.getInstance<CatalogDao>(CatalogDao)
                    .getFulfillmentGroups(filter)
                    .then((s) => {
                        resolve(s);
                    })
                    .catch((ex) => {
                        logError(ex);
                        reject();
                    });
            });
        },
    };
}
